/**
 * `HumanMessage` — renders one user-role message in the transcript.
 *
 * Visual contract mirrors Taqat's user bubble
 * (`taqat-ai-employee-store/apps/client/src/components/chat/messages/message.tsx`):
 *
 *   - File attachments sit *above* the bubble, right-aligned, with a
 *     small gap between them.
 *   - The bubble itself is a rounded-xl `bg-bg` (Flint's neutral
 *     surface, equivalent to shadcn's `bg-muted`) with `px-3 py-2`.
 *     No border, no shadow — the visual weight comes from the
 *     surface contrast.
 *   - The text content runs through the markdown renderer so simple
 *     formatting in user prompts (lists, inline code) renders cleanly.
 *   - The whole message is anchored right via `ml-auto w-fit max-w-2xl`
 *     so long messages wrap inside the bubble without pushing past
 *     the column edge.
 */
import type { FileUIPart, UIMessage } from "ai";
import { MessageResponse } from "@flint/ui";

interface HumanMessageProps {
  message: UIMessage;
}

export function HumanMessage({ message }: HumanMessageProps) {
  const fileParts = message.parts.filter((part): part is FileUIPart => part.type === "file");
  const textBody = message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("\n")
    .trim();

  return (
    <div className="ml-auto flex w-fit max-w-2xl flex-col items-end gap-2">
      {fileParts.length > 0 ? (
        <div className="flex flex-wrap justify-end gap-2">
          {fileParts.map((part, index) => (
            <div
              key={`${part.filename ?? "file"}-${index}`}
              className="rounded-md border border-line bg-paper px-3 py-2 text-xs text-ink-2"
            >
              {part.filename ?? part.mediaType}
            </div>
          ))}
        </div>
      ) : null}

      {textBody ? (
        <div className="w-fit max-w-full rounded-xl bg-bg px-3 py-2 text-base leading-7 text-ink">
          <MessageResponse className="prose max-w-none leading-[1.7] [&_p]:my-0">
            {textBody}
          </MessageResponse>
        </div>
      ) : null}
    </div>
  );
}
